import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listPublicFestivals } from "@/api/client";
import { Calendar, MapPin, Search, Users, Layers } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Input, EmptyState } from "@/components/ds";

export function PublicDiscoveryPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { data: festivals, isLoading } = useQuery({
    queryKey: ["public-festivals", debouncedSearch],
    queryFn: () => listPublicFestivals(debouncedSearch || undefined),
  });

  const handleSearch = (value: string) => {
    setSearch(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  const formatDate = (d: string) =>
    new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  const isCurrent = (start: string, end: string) => {
    const now = new Date();
    const s = new Date(start + "T00:00:00");
    const e = new Date(end + "T23:59:59");
    return now >= s && now <= e;
  };

  const isUpcoming = (start: string) => {
    return new Date(start + "T00:00:00") > new Date();
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      {/* Header */}
      <div className="mb-8 text-center">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-accent">
          Public timetables
        </p>
        <h1
          className="mt-2 text-3xl font-bold tracking-[-0.03em] sm:text-4xl"
          data-ds-display
        >
          Festival Lineups
        </h1>
        <p className="mt-2 text-sm text-ink-3">
          Browse published festival timetables
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-4" />
        <Input
          type="text"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search by festival name or location…"
          className="pl-10"
        />
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card p-5">
              <Skeleton className="h-6 w-40" />
              <div className="mt-3 flex items-center gap-3">
                <Skeleton className="h-4 w-48" />
              </div>
              <div className="mt-3 flex items-center gap-4">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : !festivals || festivals.length === 0 ? (
        search ? (
          <EmptyState
            icon={Search}
            title="No festivals match your search"
            action={
              <button
                onClick={() => {
                  setSearch("");
                  setDebouncedSearch("");
                }}
                className="text-sm text-ink-3 transition-colors hover:text-ink"
              >
                Clear search
              </button>
            }
          />
        ) : (
          <EmptyState
            icon={Calendar}
            title="No published festivals yet"
            subtitle="Check back later for upcoming festival timetables"
          />
        )
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {festivals.map((festival) => (
            <Link
              key={festival.slug}
              to={`/live/${festival.slug}`}
              className="card group p-5 transition-colors hover:border-line-3"
            >
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-lg font-bold tracking-tight text-ink">
                  {festival.name}
                </h2>
                {isCurrent(festival.start_date, festival.end_date) && (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-pill border-2 px-3 py-0.5 text-xs font-semibold text-ok"
                    style={{
                      borderColor: "color-mix(in oklab, var(--tr-ok) 35%, transparent)",
                      background: "color-mix(in oklab, var(--tr-ok) 12%, transparent)",
                    }}
                  >
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ok opacity-75" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-ok" />
                    </span>
                    Live
                  </span>
                )}
                {!isCurrent(festival.start_date, festival.end_date) &&
                  isUpcoming(festival.start_date) && (
                    <span
                      className="rounded-pill border-2 px-3 py-0.5 text-xs font-semibold text-accent"
                      style={{
                        borderColor:
                          "color-mix(in oklab, var(--tr-accent) 35%, transparent)",
                        background:
                          "color-mix(in oklab, var(--tr-accent) 12%, transparent)",
                      }}
                    >
                      Upcoming
                    </span>
                  )}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-xs text-ink-3">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" strokeWidth={1.6} />
                  {formatDate(festival.start_date)} — {formatDate(festival.end_date)}
                </span>
                {festival.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" strokeWidth={1.6} />
                    {festival.location}
                  </span>
                )}
              </div>

              <div className="mt-3 flex items-center gap-4 text-xs text-ink-4">
                <span className="inline-flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5" strokeWidth={1.6} />
                  {festival.stage_count} {festival.stage_count === 1 ? "stage" : "stages"}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" strokeWidth={1.6} />
                  {festival.artist_count} artists
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
